import { type OutfitProduct, type SuggestedAction } from '../../../../types/ai';
import {
  CART_ADD_ACTION_PREFIX,
  PRODUCT_FOLLOWUP_ACTION_PREFIX,
  PRODUCT_SELECT_ACTION_PREFIX,
  PRODUCT_SIMILAR_ACTION_PREFIX,
} from '../constants';
import type { ProductCardData } from '../types';
import { isSingleProductDetailAction } from './product-actions';

export function findCardActions(
  card: ProductCardData,
  actions: SuggestedAction[],
  allCards: ProductCardData[],
): SuggestedAction[] {
  const matches: SuggestedAction[] = [];

  for (const action of actions) {
    if (card.id && String(action.actionId ?? '') === `${PRODUCT_SELECT_ACTION_PREFIX}${card.id}`) {
      matches.push(action);
      continue;
    }

    if (card.id && String(action.actionId ?? '') === `${CART_ADD_ACTION_PREFIX}${card.id}`) {
      matches.push(action);
      continue;
    }

    if (
      card.id &&
      (String(action.actionId ?? '') === `${PRODUCT_FOLLOWUP_ACTION_PREFIX}${card.id}` ||
        String(action.actionId ?? '') === `${PRODUCT_SIMILAR_ACTION_PREFIX}${card.id}`)
    ) {
      matches.push(action);
      continue;
    }

    const selectMatch = action.value.match(/^outfit_select:[^:]+:(.+)$/i);
    if (selectMatch && card.id && selectMatch[1] === card.id) {
      matches.push(action);
      continue;
    }

    const changeMatch = action.value.match(/^outfit_change:([^:]+)/i);
    if (changeMatch && card.slot && changeMatch[1] === card.slot) {
      matches.push(action);
      continue;
    }

    if (/^jag[\s\S]{0,10}v[\s\S]{0,5}ljer?\s+/i.test(action.value)) {
      const selectedName = action.value.replace(/^jag[\s\S]{0,10}v[\s\S]{0,5}ljer?\s+/i, '').trim();
      const fullCardName = [card.brand, card.title].filter(Boolean).join(' ');
      if (
        selectedName &&
        (selectedName.localeCompare(card.title, 'sv', { sensitivity: 'accent' }) === 0 ||
          selectedName.localeCompare(fullCardName, 'sv', { sensitivity: 'accent' }) === 0)
      ) {
        matches.push(action);
        continue;
      }
    }

    if (allCards.length === 1 && isSingleProductDetailAction(action)) {
      matches.push(action);
    }
  }

  return matches.filter(
    (action, index, list) =>
      list.findIndex(
        (candidate) =>
          candidate.actionId === action.actionId &&
          candidate.label === action.label &&
          candidate.value === action.value,
      ) === index,
  );
}

export function matchSuggestedActionsToCards(
  cards: ProductCardData[],
  actions: SuggestedAction[],
): {
  cardActions: SuggestedAction[][];
  consumedActionIndexes: Set<number>;
} {
  const consumedActionIndexes = new Set<number>();
  const cardActions = cards.map((card) => {
    const availableActions = actions.filter((_, index) => !consumedActionIndexes.has(index));
    const matches = findCardActions(card, availableActions, cards);

    for (const match of matches) {
      const index = actions.findIndex(
        (candidate, candidateIndex) =>
          !consumedActionIndexes.has(candidateIndex) &&
          candidate.actionId === match.actionId &&
          candidate.label === match.label &&
          candidate.value === match.value,
      );
      if (index >= 0) {
        consumedActionIndexes.add(index);
      }
    }

    return matches;
  });

  return { cardActions, consumedActionIndexes };
}

export function findStructuredOutfitActions(
  product: OutfitProduct,
  actions: SuggestedAction[],
): SuggestedAction[] {
  const productId = String(product.id ?? '').trim();
  const slot = String(product.categoryKey ?? product.role ?? '').trim();
  if (!productId && !slot) return [];

  return actions
    .filter((action) => {
      const actionId = String(action.actionId ?? '');
      const value = String(action.value ?? '');

      if (
        productId &&
        (actionId === `${PRODUCT_SELECT_ACTION_PREFIX}${productId}` ||
          value === `${PRODUCT_SELECT_ACTION_PREFIX}${productId}`)
      ) {
        return true;
      }

      const selectMatch = value.match(/^outfit_select:[^:]+:(.+)$/i);
      if (productId && selectMatch?.[1] === productId) return true;

      const changeMatch = value.match(/^outfit_change:([^:]+)/i);
      if (slot && changeMatch?.[1] === slot) return true;

      return false;
    })
    .filter(
      (action, index, list) =>
        list.findIndex(
          (candidate) =>
            candidate.actionId === action.actionId &&
            candidate.label === action.label &&
            candidate.value === action.value,
        ) === index,
    );
}

export function matchSuggestedActionsToStructuredOutfit(
  products: OutfitProduct[],
  actions: SuggestedAction[],
): {
  productActions: SuggestedAction[][];
  consumedActionIndexes: Set<number>;
} {
  const consumedActionIndexes = new Set<number>();
  const productActions = products.map((product) => {
    const availableActions = actions.filter((_, index) => !consumedActionIndexes.has(index));
    const matches = findStructuredOutfitActions(product, availableActions);

    for (const match of matches) {
      const index = actions.findIndex(
        (candidate, candidateIndex) =>
          !consumedActionIndexes.has(candidateIndex) &&
          candidate.actionId === match.actionId &&
          candidate.label === match.label &&
          candidate.value === match.value,
      );
      if (index >= 0) {
        consumedActionIndexes.add(index);
      }
    }

    return matches;
  });

  return { productActions, consumedActionIndexes };
}
